// components/SessionExpirationHandler.tsx
'use client';

import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useToastContext } from '@/components/ui/ToastProvider';
import { useRouter, usePathname } from 'next/navigation';
import { isSessionValid, clearAuthCookies, decodeJWT } from '@/lib/utils/jwt';
import { logout } from '@/lib/slices/authSlice';
import Cookies from 'js-cookie';

export default function SessionExpirationHandler() {
  const { toast } = useToastContext();
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const user = useSelector((state: any) => state.auth.user);
  const [hasShownExpiredToast, setHasShownExpiredToast] = useState(false);

  // Determine if current page requires authentication
  const isProtectedRoute = useCallback(() => {
    // Exclude auth pages from protection check
    const authPages = [
      '/sign-in',
      '/admin/sign-in',
      '/cashier/sign-in',
      '/reset-password',
      '/admin/reset-password',
      '/cashier/reset-password',
      '/create-account'
    ];
    
    if (authPages.some(page => pathname?.startsWith(page))) {
      return false;
    }
    
    return pathname?.startsWith('/dashboard') || pathname?.startsWith('/admin');
  }, [pathname]);

  // Get appropriate login URL based on role
  const getLoginUrl = useCallback(() => {
    const accessToken = Cookies.get('accessToken');
    if (accessToken) {
      const decoded = decodeJWT(accessToken);
      const userRole = decoded?.role?.toUpperCase();
      const adminRoles = ['CASHIER', 'FINANCIAL', 'ADMIN', 'SUPER_ADMIN', 'DECK_HANDLER', 'STANDARD'];
      if (adminRoles.includes(userRole || '')) {
        return '/admin/sign-in';
      }
    }
    // Check pathname as fallback
    if (pathname?.startsWith('/admin')) {
      return '/admin/sign-in';
    }
    return '/sign-in';
  }, [pathname]);

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    if (hasShownExpiredToast) return;
    
    const loginUrl = getLoginUrl();
    
    // Clear auth state
    clearAuthCookies();
    dispatch(logout());
    
    // Show toast only once
    setHasShownExpiredToast(true);
    toast.error('', 'Your session has expired. Please sign in again.');
    
    // Redirect to login
    router.push(loginUrl);
  }, [hasShownExpiredToast, getLoginUrl, dispatch, toast, router]);

  // Check session validity periodically
  useEffect(() => {
    // Don't check on auth pages at all
    if (!isProtectedRoute()) return;
    
    // Only check if user is logged in or on protected route
    if (!user) return;

    const checkSession = () => {
      if (!isSessionValid()) {
        handleSessionExpired();
      }
    };

    // Check immediately
    checkSession();

    // Check every 5 seconds for faster detection
    const interval = setInterval(checkSession, 5000);

    // Also check on window focus (user returns to tab)
    const handleFocus = () => checkSession();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, isProtectedRoute, handleSessionExpired]);

  // Reset the toast flag when user logs in
  useEffect(() => {
    if (user) {
      setHasShownExpiredToast(false);
    }
  }, [user]);

  return null; // This component doesn't render anything
}